import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const AuthContext = createContext(null);

const SESSION_STORAGE_KEY = 'doctriage_session_token';

const customPort = 8000;
const baseUrl = process.env.REACT_APP_API_URL ||
  window.location.protocol + '//' + window.location.hostname + ':' + customPort;

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEY));

  const isAuthenticated = Boolean(token);

  const login = useCallback(async (password) => {
    const response = await fetch(`${baseUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Login failed');
    }

    const data = await response.json();
    sessionStorage.setItem(SESSION_STORAGE_KEY, data.token);
    setToken(data.token);
  }, []);

  const logout = useCallback(async () => {
    const currentToken = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (currentToken) {
      try {
        await fetch(`${baseUrl}/auth/logout`, {
          method: 'POST',
          headers: { 'X-API-Key': currentToken },
        });
      } catch {
        // Best-effort server-side cleanup
      }
    }
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setToken(null);
  }, []);

  const value = useMemo(() => ({
    token,
    isAuthenticated,
    login,
    logout,
  }), [token, isAuthenticated, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
